import { useTranslation } from 'react-i18next'
import AmazonSalesBanner from '../components/AmazonSalesBanner'
import CuratedPhotosHeader from '../components/CuratedPhotosHeader'
import DPFFaqJpSection from '../components/DPFFaqJpSection'
import EaseOfUseSection from '../components/EaseOfUseSection'
import EmotionalClosingSection from '../components/EmotionalClosingSection'
import Footer from '../components/Footer'
import HeroSection from '../components/HeroSection'
import HeroSectionUs from '../components/HeroSectionUs'
import MemoriesSection from '../components/MemoriesSection'
import ProductPricingSection from '../components/ProductPricingSection'
import PurchaseButtons from '../components/PurchaseButtons'
import PurchaseStepsSection from '../components/PurchaseStepsSection'
import UsageScenesSection, { UsageScene } from '../components/UsageScenesSection'
import UserVoicesSection from '../components/UserVoicesSection'
import { landingAssetsForLocale } from '../config/landingAssets'
import useLandingPageState from '../hooks/useLandingPageState'
import { FrameConfiguration } from '../types'

const YOUTUBE_VIDEO_IDS = {
  jp: 'xYhEvqhHkBw',
  us: 'xyWZjZa0PX4',
}

const JP_HEADER_WIDTH = 780
const JP_HEADER_HEIGHT = 802
const US_HEADER_WIDTH = 750
const US_HEADER_HEIGHT = 816

const DPF_JP_FRAMES_REGULAR: FrameConfiguration = {
  width: JP_HEADER_WIDTH,
  height: JP_HEADER_HEIGHT,
  requiredPhotos: 6,
  frames: [
    {
      left: (200 / JP_HEADER_WIDTH) * 100,
      top: (376 / JP_HEADER_HEIGHT) * 100,
      width: (374 / JP_HEADER_WIDTH) * 100,
      height: (228 / JP_HEADER_HEIGHT) * 100,
      rotate: 0,
    },
  ],
}

const DPF_US_FRAMES_REGULAR: FrameConfiguration = {
  width: US_HEADER_WIDTH,
  height: US_HEADER_HEIGHT,
  requiredPhotos: 2,
  frames: [
    {
      left: (182 / US_HEADER_WIDTH) * 100,
      top: (408 / US_HEADER_HEIGHT) * 100,
      width: (398 / US_HEADER_WIDTH) * 100,
      height: (254 / US_HEADER_HEIGHT) * 100,
      rotate: 0,
    },
  ],
}

export default function LandingPage() {
  const { i18n } = useTranslation('landingPage')
  const Page = i18n.language.startsWith('en') ? LandingPageUs : LandingPageJp

  return <Page />
}

function SoldOutRestockBanner() {
  const { t } = useTranslation('landingPage')
  const [title, ...bodyLines] = t('soldOut.sticky').split('\n')

  return (
    <div className='sticky top-0 z-20 bg-mi-bg-primary px-4 py-5 text-center text-white'>
      <p className='text-[17px] font-bold leading-6'>{title}</p>
      {bodyLines.length > 0 && (
        <p className='mt-1 text-sm font-normal leading-5'>
          {bodyLines.map((line, index) => (
            <span key={index}>
              {index > 0 && <br />}
              {line}
            </span>
          ))}
        </p>
      )}
    </div>
  )
}

function HeroIntroUs() {
  const { t } = useTranslation('landingPage')

  return (
    <section className='px-4 py-12'>
      <h1 className='text-gray-12 whitespace-pre-line text-center text-base font-normal leading-relaxed'>
        {t('hero.intro')}
      </h1>
    </section>
  )
}

function LandingPageJp() {
  const assets = landingAssetsForLocale('ja')
  const usageScenes: UsageScene[] = [
    {
      type: 'carousel',
      images: [...assets.carouselImages],
      titleKey: 'usageScenes.scenes.0.title',
      descriptionKey: 'usageScenes.scenes.0.description',
    },
    {
      type: 'single',
      images: [assets.presentImg1],
      titleKey: 'usageScenes.scenes.1.title',
      descriptionKey: 'usageScenes.scenes.1.description',
    },
  ]
  const {
    handleBuyOnAmazonClick,
    handleBuyOnRakutenClick,
    isAllSoldOut,
    isAmazonSaleActive,
    isPremiumUser,
    isUsingFallback,
    logClickedLearnMoreAboutPremium,
    logClickedPurchaseStepsButtonAmazon,
    logClickedPurchaseStepsButtonRakuten,
    premiumDeepLinkUrl,
    purchaseChannels,
    userPhotos,
  } = useLandingPageState()

  return (
    <>
      {isAllSoldOut && <SoldOutRestockBanner />}

      {isAmazonSaleActive && <AmazonSalesBanner />}

      <header className='relative'>
        <CuratedPhotosHeader
          curatedPhotos={userPhotos}
          frameConfiguration={DPF_JP_FRAMES_REGULAR}
          headerImages={assets.headers.regular}
          isUsingFallback={isUsingFallback}
        />
      </header>

      <UserVoicesSection />
      <HeroSection />
      <EaseOfUseSection
        headingKey='easeOfUse.title'
        showDescription={true}
        videoId={YOUTUBE_VIDEO_IDS.jp}
      />

      <ProductPricingSection
        isPremiumUser={isPremiumUser}
        premiumDeepLinkUrl={premiumDeepLinkUrl}
        purchaseChannels={purchaseChannels}
        onAmazonClick={handleBuyOnAmazonClick}
        logClickedLearnMoreAboutPremium={logClickedLearnMoreAboutPremium}
      />
      <PurchaseStepsSection
        logClickedPurchaseStepsButtonAmazon={logClickedPurchaseStepsButtonAmazon}
        logClickedPurchaseStepsButtonRakuten={logClickedPurchaseStepsButtonRakuten}
        premiumFeaturesLayout='list'
        purchaseChannels={purchaseChannels}
      />

      <UsageScenesSection
        contentClassName='flex flex-col gap-6 [&>h2]:mb-2'
        scenes={usageScenes}
        sectionClassName='bg-[#F5F5F5] px-4 py-10'
        showHeading={true}
        sceneClassName='space-y-6'
      />
      <MemoriesSection curatedPhotos={[]} isUsingFallback={true} />
      <EmotionalClosingSection />
      <DPFFaqJpSection />
      <Footer />

      <PurchaseButtons
        onAmazonClick={handleBuyOnAmazonClick}
        onRakutenClick={handleBuyOnRakutenClick}
        purchaseChannels={purchaseChannels}
      />
    </>
  )
}

function LandingPageUs() {
  const assets = landingAssetsForLocale('en')
  const usageScenes: UsageScene[] = [
    {
      type: 'single',
      images: [assets.carouselImages[0]],
      titleKey: 'usageScenes.scenes.0.title',
      descriptionKey: 'usageScenes.scenes.0.description',
      imageClassName: 'mb-6 w-full',
    },
  ]
  const {
    handleBuyOnAmazonClick,
    handleBuyOnRakutenClick,
    isAllSoldOut,
    isAmazonSaleActive,
    isPremiumUser,
    isUsingFallback,
    logClickedLearnMoreAboutPremium,
    logClickedPurchaseStepsButtonAmazon,
    logClickedPurchaseStepsButtonRakuten,
    premiumDeepLinkUrl,
    purchaseChannels,
    userPhotos,
  } = useLandingPageState()

  return (
    <>
      {isAllSoldOut && <SoldOutRestockBanner />}

      {isAmazonSaleActive && <AmazonSalesBanner />}

      <header className='relative'>
        <CuratedPhotosHeader
          curatedPhotos={userPhotos}
          frameConfiguration={DPF_US_FRAMES_REGULAR}
          headerImages={assets.headers.regular}
          isUsingFallback={isUsingFallback}
        />
      </header>

      <HeroIntroUs />
      <HeroSectionUs />
      <EaseOfUseSection
        headingKey='easeOfUse.videoHeading'
        showDescription={false}
        videoId={YOUTUBE_VIDEO_IDS.us}
      />

      <ProductPricingSection
        isPremiumUser={isPremiumUser}
        premiumDeepLinkUrl={premiumDeepLinkUrl}
        purchaseChannels={purchaseChannels}
        onAmazonClick={handleBuyOnAmazonClick}
        logClickedLearnMoreAboutPremium={logClickedLearnMoreAboutPremium}
      />
      <PurchaseStepsSection
        logClickedPurchaseStepsButtonAmazon={logClickedPurchaseStepsButtonAmazon}
        logClickedPurchaseStepsButtonRakuten={logClickedPurchaseStepsButtonRakuten}
        premiumFeaturesLayout='grid'
        purchaseChannels={purchaseChannels}
      />

      <UsageScenesSection
        scenes={usageScenes}
        sectionClassName='bg-[#EAF7FC] py-12'
        showHeading={false}
        sceneContentWrapperClassName='px-4'
      />
      <DPFFaqJpSection />
      <Footer />

      <PurchaseButtons
        onAmazonClick={handleBuyOnAmazonClick}
        onRakutenClick={handleBuyOnRakutenClick}
        purchaseChannels={purchaseChannels}
      />
    </>
  )
}
